/*
 * AlumierMD gate — client-side verification + enforcement.
 *
 * Whenever an AlumierMD product is in the cart, this script BLOCKS the checkout
 * button(s) itself (rather than trusting the server-rendered `disabled`, which
 * some of Beautech's button variants don't honour on the visible element). It
 * then calls the AlumierMD endpoint and, only on a "true" response, unblocks
 * checkout and removes the notice.
 *
 * Re-runs on every cart change. Beautech swaps <xo-cart-will-change> regions on
 * update, so a MutationObserver watching for added nodes is a reliable hook.
 * A successful result is cached for the session.
 *
 * Works with markup from snippets/alumier-notice.liquid. Checkout buttons are
 * matched by `data-alumier-checkout` OR by `name="checkout"` (fallback), so it
 * does not depend on the `attrs` passthrough having reached the button element.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://www.viastudios.co.uk/alumiermd.php';
  var VERIFIED_KEY = 'alumierVerified';
  var CONTACT_URL = '/pages/contact-us';
  var BLOCK_ATTR = 'data-alumier-blocked';

  function verifiedThisSession() {
    try { return sessionStorage.getItem(VERIFIED_KEY) === 'true'; } catch (e) { return false; }
  }
  function rememberVerified() {
    try { sessionStorage.setItem(VERIFIED_KEY, 'true'); } catch (e) {}
  }

  function checkoutButtons() {
    return document.querySelectorAll('[data-alumier-checkout], [name="checkout"]');
  }
  function notices() { return document.querySelectorAll('[data-alumier-notice]'); }

  // Apply our own block. Real <button> -> disabled stops submit; pointer-events +
  // the capture-phase click handler below also neutralise anchor-styled buttons.
  function block(btn) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute(BLOCK_ATTR, '');
    btn.style.pointerEvents = 'none';
    btn.style.opacity = '0.5';
    btn.style.cursor = 'not-allowed';
  }

  // Fully clear a block (used when verified — the cart definitely has items, so
  // there is no empty-cart disable to worry about here).
  function unblock(btn) {
    btn.removeAttribute(BLOCK_ATTR);
    btn.disabled = false;
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-disabled');
    btn.style.pointerEvents = '';
    btn.style.opacity = '';
    btn.style.cursor = '';
  }

  // Clear ONLY a block we applied. Used when nothing is gated, so we never
  // re-enable a button the theme disabled for another reason (e.g. empty cart).
  function clearOurBlock(btn) {
    if (btn.hasAttribute(BLOCK_ATTR)) unblock(btn);
  }

  function errorCode(text) {
    var stripped = String(text).replace(/[.,\/#!$%\^&\*;:{}=\-_'`~()]/g, '');
    return (stripped.match(/(\b\S)?/g) || []).join('').toUpperCase();
  }

  function showError(notice, responseText) {
    var textEl = notice.querySelector('[data-alumier-notice-text]');
    if (textEl) {
      textEl.innerHTML =
        'Sorry, we had a problem confirming your AlumierMD registration.<br>' +
        'Please contact us for help, quoting error code: ' +
        '<strong style="letter-spacing:1px;">' + errorCode(responseText) + '</strong>';
    }
    var action = notice.querySelector('[data-alumier-notice-action]');
    if (!action) {
      action = document.createElement('a');
      action.className = 'alumier-notice__action';
      action.setAttribute('data-alumier-notice-action', '');
      action.setAttribute(
        'style',
        'flex:0 0 auto; display:inline-flex; align-items:center; padding:.9rem 1.8rem;' +
        'background:var(--alumier-red,#c0392b); color:#fff; border:1px solid var(--alumier-red,#c0392b);' +
        'border-radius:var(--border-radius,.6rem); text-decoration:none;' +
        'white-space:nowrap; font-weight:600;'
      );
      notice.appendChild(action);
    }
    action.href = CONTACT_URL;
    action.textContent = 'Contact us';
  }

  var inFlight = false;

  function removeNotices() {
    notices().forEach(function (n) { n.parentNode && n.parentNode.removeChild(n); });
  }

  function run() {
    var all = notices();
    var gated = all.length > 0;

    if (!gated) {
      // Nothing gated right now: clear only blocks we set, leave others alone.
      checkoutButtons().forEach(clearOurBlock);
      return;
    }

    if (verifiedThisSession()) {
      checkoutButtons().forEach(unblock);
      removeNotices();
      return;
    }

    // Gated and not verified — enforce the block immediately.
    checkoutButtons().forEach(block);

    // Only a logged-in notice carries data we can verify against.
    var loggedIn = null;
    all.forEach(function (n) { if (n.getAttribute('data-logged-in') === 'true') loggedIn = n; });
    if (!loggedIn) return;          // logged out: leave prompt + block in place
    if (inFlight) return;

    var url = ENDPOINT +
      '?email=' + encodeURIComponent(loggedIn.getAttribute('data-email') || '') +
      '&fname=' + encodeURIComponent(loggedIn.getAttribute('data-fname') || '') +
      '&sname=' + encodeURIComponent(loggedIn.getAttribute('data-sname') || '');

    inFlight = true;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (data) {
        inFlight = false;
        if (String(data).trim() === 'true') {
          rememberVerified();
          checkoutButtons().forEach(unblock);
          removeNotices();
        } else {
          notices().forEach(function (n) {
            if (n.getAttribute('data-logged-in') === 'true') showError(n, data);
          });
        }
      })
      .catch(function (err) {
        inFlight = false;
        notices().forEach(function (n) {
          if (n.getAttribute('data-logged-in') === 'true') showError(n, 'NE ' + (err && err.message || ''));
        });
      });
  }

  var timer;
  function schedule() { clearTimeout(timer); timer = setTimeout(run, 120); }

  function init() {
    // Final safety net: swallow clicks/keyboard activation on anything we blocked,
    // in case a button variant is an anchor that ignores `disabled`.
    document.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('[' + BLOCK_ATTR + ']') : null;
      if (el) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    run();

    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { schedule(); return; }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
